import React, { useEffect, useMemo, useState } from 'react';
import { LOG_FILTERS, ICON_BGS, TAG_STYLES, LogType } from '../data';
import { InstituteAutomationService, AuditRow } from '../instituteAutomationService';

// Map a raw audit `action` code → display type/icon/tag.
const classify = (action: string): { type: LogType; icon: string; tag: string } => {
    const a = (action || '').toLowerCase();
    if (a.includes('sent') || a.includes('deliver') || a.includes('send')) return { type: 'send', icon: '📧', tag: 'Report Sent' };
    if (a.includes('approve')) return { type: 'approval', icon: '✅', tag: 'Approval' };
    if (a.includes('schedule')) return { type: 'schedule', icon: '📅', tag: 'Schedule' };
    if (a.startsWith('global')) return { type: 'global', icon: '⚙️', tag: 'Global Settings' };
    if (a.includes('override') || a.startsWith('client')) return { type: 'override', icon: '🔧', tag: 'Override' };
    return { type: 'system', icon: '🤖', tag: 'System' };
};

// Humanize an action code: "client_component_override_updated" → "Client component override updated".
const humanize = (action: string) => {
    const s = (action || '').replace(/_/g, ' ').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Event';
};

// Trim trailing decimal zeros: "2.0000" → "2", "1.5000" → "1.5"; passes non-numbers through.
const trimNum = (v: unknown): string => {
    const s = String(v);
    return /^-?\d+\.\d+$/.test(s) ? String(Number(s)) : s;
};

// Parse a before/after JSON value (may arrive as string or object) into a compact one-line summary.
const summarize = (val: unknown): string | null => {
    if (val == null) return null;
    let obj: unknown = val;
    if (typeof val === 'string') {
        try { obj = JSON.parse(val); } catch { return trimNum(val); }
    }
    if (obj == null) return null;
    if (typeof obj !== 'object') return trimNum(obj);
    const parts = Object.entries(obj as Record<string, unknown>)
        .filter(([, v]) => v != null && typeof v !== 'object')
        .map(([k, v]) => `${k}: ${trimNum(v)}`);
    return parts.length ? parts.join(' · ') : null;
};

interface DisplayEntry {
    id: number; type: LogType; icon: string; tag: string; desc: string;
    date: string; time: string; actorSystem: boolean; actor: string; client: string;
    diff: { old: string; new: string } | null;
}

const toDisplay = (r: AuditRow): DisplayEntry => {
    const { type, icon, tag } = classify(r.action);
    const d = new Date(r.performed_at);
    const valid = !isNaN(d.getTime());
    const before = summarize(r.before_value);
    const after = summarize(r.after_value);
    const fullName = [r.actor_first_name, r.actor_last_name].filter(Boolean).join(' ').trim();
    return {
        id: r.id, type, icon, tag,
        desc: humanize(r.action) + (r.reason ? ` — ${r.reason}` : ''),
        date: valid ? d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
        time: valid ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '',
        actorSystem: !r.actor_id,
        // Prefer the resolved user name; fall back to the UUID prefix, then System.
        actor: fullName || (r.actor_id ? r.actor_id.slice(0, 8) : 'System'),
        client: r.institute_id
            ? (r.institute_name ? `${r.institute_name} (${r.institute_id})` : `Institute ${r.institute_id}`)
            : 'All Clients',
        diff: (before || after) ? { old: before ?? '—', new: after ?? '—' } : null,
    };
};

const LogDrawer: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const [filter, setFilter] = useState<LogType | 'all'>('all');
    const [query, setQuery] = useState('');
    const [rows, setRows] = useState<DisplayEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    // Load the audit trail whenever the drawer opens.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true); setError(false);
        (async () => {
            try {
                const data = await InstituteAutomationService.getAuditLog({ page: 1 });
                if (!cancelled) setRows(data.map(toDisplay));
            } catch (_) {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.toLowerCase();
        return rows.filter((e) => {
            const matchFilter = filter === 'all' || e.type === filter;
            const matchSearch = !q || e.desc.toLowerCase().includes(q) ||
                e.client.toLowerCase().includes(q) || e.actor.toLowerCase().includes(q) || e.tag.toLowerCase().includes(q);
            return matchFilter && matchSearch;
        });
    }, [filter, query, rows]);

    // Group by date preserving order.
    const groups = useMemo(() => {
        const map: Record<string, DisplayEntry[]> = {};
        const order: string[] = [];
        filtered.forEach((e) => { if (!map[e.date]) { map[e.date] = []; order.push(e.date); } map[e.date].push(e); });
        return order.map((date) => [date, map[date]] as [string, DisplayEntry[]]);
    }, [filtered]);

    // Live counts per type for the summary strip.
    const counts = useMemo(() => {
        const c: Record<string, number> = { send: 0, override: 0, system: 0, global: 0 };
        rows.forEach((e) => { if (c[e.type] != null) c[e.type] += 1; });
        return c;
    }, [rows]);

    return (
        <>
            <div className={`iar-backdrop${open ? ' show' : ''}`} style={{ display: open ? 'block' : 'none' }} onClick={onClose} />
            <div className={`offcanvas offcanvas-end bg-white${open ? ' show' : ''}`} tabIndex={-1} style={{ width: 620, maxWidth: '95vw', visibility: open ? 'visible' : 'hidden' }}>
                <div className="offcanvas-header border-bottom flex-column align-items-stretch pb-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="offcanvas-title font-15 font-600 text-dark d-flex align-items-center gap-2">
                            📋 Report Module Audit Log
                            <span className="badge bg-light text-secondary rounded-pill font-11 px-2 py-0.5">{filtered.length} entries</span>
                        </h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
                    </div>

                    <div className="d-flex align-items-center gap-2 bg-light border rounded px-3 py-2 mb-2">
                        <span className="text-secondary font-14">🔍</span>
                        <input type="text" className="form-control form-control-sm border-0 bg-transparent p-0 font-13 shadow-none" placeholder="Search by client, SM, component…" value={query} onChange={(e) => setQuery(e.target.value)} />
                        <button className="btn btn-link text-secondary p-0 border-0 shadow-none" onClick={() => setQuery('')}>✕</button>
                    </div>

                    <div className="d-flex flex-wrap gap-1 mt-1">
                        {LOG_FILTERS.map((f) => {
                            const active = filter === f.key;
                            return (
                                <button
                                    key={f.key}
                                    className={`btn btn-sm font-11 rounded-pill px-3 py-0.5 ${active ? 'bg-dark text-white' : f.className}`}
                                    style={active ? f.activeStyle : undefined}
                                    onClick={() => setFilter(f.key)}
                                >
                                    {f.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="row g-0 text-center border-bottom border-light bg-light">
                    <div className="col border-end border-light py-2"><div className="font-16 font-600 text-success">{counts.send}</div><div className="font-9 text-uppercase text-secondary font-600">Sends</div></div>
                    <div className="col border-end border-light py-2"><div className="font-16 font-600 text-warning">{counts.override}</div><div className="font-9 text-uppercase text-secondary font-600">Overrides</div></div>
                    <div className="col border-end border-light py-2"><div className="font-16 font-600 text-primary">{counts.system}</div><div className="font-9 text-uppercase text-secondary font-600">System</div></div>
                    <div className="col py-2"><div className="font-16 font-600" style={{ color: '#7c3aed' }}>{counts.global}</div><div className="font-9 text-uppercase text-secondary font-600">Global</div></div>
                </div>

                <div className="offcanvas-body bg-white" style={{ overflowY: 'auto' }}>
                    {loading ? (
                        <div className="text-center text-secondary py-5"><span className="spinner-border spinner-border-sm me-2" />Loading audit log…</div>
                    ) : error ? (
                        <div className="text-center py-5 text-secondary">
                            <div className="font-36 mb-2">⚠️</div>
                            <h6 className="font-13 font-600 text-dark mb-1">Couldn't load the audit log</h6>
                            <p className="font-12 mb-0">Please try again.</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-5 text-secondary">
                            <div className="font-36 mb-2">🔍</div>
                            <h6 className="font-13 font-600 text-dark mb-1">{rows.length === 0 ? 'No audit entries yet' : 'No matching log entries'}</h6>
                            <p className="font-12 mb-0">{rows.length === 0 ? 'Audit entries appear here as settings change and reports run.' : 'Try adjusting your search or filter'}</p>
                        </div>
                    ) : groups.map(([date, entries]) => (
                        <div className="mt-3" key={date}>
                            <div className="sticky-top bg-white py-1 font-11 font-600 text-secondary text-uppercase border-bottom border-light mb-2" style={{ zIndex: 10 }}>{date}</div>
                            {entries.map((e) => (
                                <div className="d-flex gap-3 py-2 border-bottom border-light align-items-start" key={e.id}>
                                    <div className="d-flex align-items-center justify-content-center rounded font-16" style={{ width: 32, height: 32, flexShrink: 0, backgroundColor: ICON_BGS[e.type] }}>{e.icon}</div>
                                    <div className="flex-grow-1 min-w-0">
                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                            <div className="font-12 text-dark font-500 mb-0">{e.desc}</div>
                                            <div className="font-10 text-secondary flex-shrink-0 mt-0.5">{e.time}</div>
                                        </div>
                                        <div className="d-flex flex-wrap gap-2 mt-1 align-items-center">
                                            {e.actorSystem
                                                ? <span className="font-11 text-secondary">🤖 System</span>
                                                : <span className="font-11 text-primary fw-semibold">👤 {e.actor}</span>}
                                            <span className="badge bg-light text-secondary border border-light font-10 font-500 px-2 py-0.5">{e.client}</span>
                                            <span className="badge font-10 font-500 px-2 py-0.5 rounded" style={TAG_STYLES[e.type]}>{e.tag}</span>
                                        </div>
                                        {e.diff && (
                                            <div className="d-flex align-items-center gap-2 mt-2 p-2 bg-light rounded font-11" style={{ border: '1px solid #e1e8f5' }}>
                                                <span className="text-danger text-decoration-line-through">{e.diff.old}</span>
                                                <span className="text-secondary">→</span>
                                                <span className="text-success font-600">{e.diff.new}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default LogDrawer;
